import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Header from '../Header';
import { cityService, freezoneService, pricingService } from '../../services/cityService';
import PricingStructureForm from './PricingStructureForm';

const formatAED = (value) => `AED ${Number(value || 0).toLocaleString()}`;

const getLowestLicenseFee = (packages = []) => {
  const fees = packages.flatMap((pkg) =>
    Object.values(pkg.licenseFee || {}).map((amount) => Number(amount || 0))
  );
  const positive = fees.filter((fee) => fee > 0);
  return positive.length > 0 ? Math.min(...positive) : 0;
};

const getMaxVisa = (packages = []) =>
  packages.reduce((max, pkg) => Math.max(max, Number(pkg.visa || 0)), 0);

const normalizeFreezoneCode = (value) => {
  const upper = String(value || '').trim().toUpperCase();
  if (
    upper.includes('IFZA') ||
    upper.includes('INTERNATIONAL FREE ZONE AUTHORITY')
  ) {
    return 'IFZA';
  }
  if (
    upper.includes('SHAMS') ||
    upper.includes('SHARJAH MEDIA CITY') ||
    upper.includes('SHARJAH MEDIA')
  ) {
    return 'SHAMS';
  }
  return upper;
};

const FreezoneDetail = () => {
  const { cityId: cityIdParam, freezoneId: freezoneIdParam } = useParams();

  const [city, setCity] = useState(null);
  const [freezone, setFreezone] = useState(null);
  const [pricingSets, setPricingSets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPricingForm, setShowPricingForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);

  const cityId = cityIdParam;
  const freezoneId = freezoneIdParam;

  const isValidCityId = cityId && cityId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cityId);
  const isValidFreezoneId = freezoneId && freezoneId.length === 24 && /^[0-9a-fA-F]{24}$/.test(freezoneId);
  const isValidIds = isValidCityId && isValidFreezoneId;

  const freezoneCode = useMemo(
    () => normalizeFreezoneCode(freezone?.name || ''),
    [freezone]
  );

  const fetchFreezoneAndPricing = async () => {
    try {
      setLoading(true);

      const [cityResponse, freezoneResponse] = await Promise.all([
        cityService.getCityById(cityId),
        freezoneService.getFreezoneById(freezoneId),
      ]);

      const cityData = cityResponse?.city || cityResponse;
      const freezoneData = freezoneResponse?.freezone || freezoneResponse;

      setCity(cityData);
      setFreezone(freezoneData);

      const code = normalizeFreezoneCode(freezoneData?.name || '');
      const pricingRows = await pricingService.getPricingForFreezone(code, {
        freezoneName: freezoneData?.name,
      });
      setPricingSets(pricingRows);
    } catch (error) {
      console.error('Error fetching freezone and pricing:', error);
      toast.error('Failed to load freezone pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isValidIds) {
      setLoading(false);
      return;
    }

    fetchFreezoneAndPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch function intentionally stable for route ids
  }, [cityId, freezoneId, isValidIds]);

  const handleCreateClick = () => {
    setEditingPricing(null);
    setShowPricingForm(true);
  };

  const handleEditClick = (pricing) => {
    setEditingPricing(pricing);
    setShowPricingForm(true);
  };

  const handlePricingSubmit = async (payload) => {
    await pricingService.createPricing(payload);
    await fetchFreezoneAndPricing();
  };

  const handleDeletePricing = async (pricing) => {
    const scopeLabel = pricing.licenseType
      ? `${pricing.freezoneId} (${pricing.licenseType})`
      : pricing.freezoneId;

    if (!window.confirm(`Delete pricing set ${scopeLabel}?`)) {
      return;
    }

    try {
      await pricingService.deletePricing({
        freezoneId: pricing.freezoneId,
        licenseType: pricing.licenseType || null,
      });
      toast.success('Pricing set deleted');
      await fetchFreezoneAndPricing();
    } catch (error) {
      console.error('Error deleting pricing set:', error);
      toast.error('Failed to delete pricing set');
    }
  };

  if (!isValidIds) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <h2 className="text-xl font-bold text-red-900">Invalid ID</h2>
            <p className="mt-1 text-sm text-red-700">The city or freezone ID is invalid.</p>
            <Link
              to="/cities"
              className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Back to Cities
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-72 rounded bg-gray-200" />
            <div className="h-64 rounded bg-gray-200" />
            <div className="h-56 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }

  if (!city || !freezone) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
            <h2 className="text-xl font-bold text-gray-900">Freezone not found</h2>
            <p className="mt-1 text-sm text-gray-600">Could not load this freezone.</p>
            <Link
              to={`/cities/${cityId}`}
              className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Back to City
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center gap-2 text-sm">
            <li>
              <Link to="/cities" className="inline-flex items-center text-gray-600 hover:text-blue-600">
                <ArrowLeftIcon className="mr-1 h-4 w-4" />
                Cities
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to={`/cities/${cityId}`} className="text-gray-600 hover:text-blue-600">
                {city.name}
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="font-medium text-gray-800">{freezone.name}</li>
          </ol>
        </nav>

        <section className="mb-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="relative h-52 bg-gradient-to-r from-blue-700 via-sky-600 to-cyan-500">
            {freezone.image_url && (
              <img src={freezone.image_url} alt={freezone.name} className="h-full w-full object-cover opacity-40" />
            )}
            <div className="absolute inset-0 bg-slate-900/30" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h1 className="text-3xl font-bold">{freezone.name}</h1>
              <p className="mt-1 text-sm text-white/90">Pricing and package setup for company formation flow</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 border-t border-slate-200 p-5 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{freezone.status || 'active'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Timeline</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{freezone.timeline || 'Not set'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pricing Sets</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{pricingSets.length}</p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Company Formation Pricing</h2>
              <p className="text-sm text-slate-600">
                Manage package fees and additional charges stored in the central pricing collection.
              </p>
            </div>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Pricing Set
            </button>
          </div>

          <div className="p-6">
            {pricingSets.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-slate-400" />
                <h3 className="mt-3 text-base font-semibold text-slate-900">No pricing configured</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Create the first pricing set for <span className="font-semibold">{freezoneCode}</span>.
                </p>
                <button
                  onClick={handleCreateClick}
                  className="mt-5 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Create Pricing
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {pricingSets
                  .slice()
                  .sort((a, b) => String(a.licenseType || '').localeCompare(String(b.licenseType || '')))
                  .map((pricing) => {
                    const lowestFee = getLowestLicenseFee(pricing.packages);
                    const maxVisa = getMaxVisa(pricing.packages);
                    const additionalChargeCount = Object.keys(pricing.additionalCharges || {}).length;
                    const packageLabel = pricing.licenseType
                      ? pricing.licenseType.charAt(0).toUpperCase() + pricing.licenseType.slice(1)
                      : 'Default';

                    return (
                      <article key={pricing.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-4 flex items-start justify-between">
                          <div>
                            <p className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                              {packageLabel}
                            </p>
                            <h3 className="mt-2 text-lg font-semibold text-slate-900">{freezoneCode} Pricing Set</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditClick(pricing)}
                              className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <PencilIcon className="mr-1 h-4 w-4" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePricing(pricing)}
                              className="inline-flex items-center rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                            >
                              <TrashIcon className="mr-1 h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </div>

                        <div className="mb-4 grid grid-cols-3 gap-3">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">From</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">
                              {lowestFee > 0 ? formatAED(lowestFee) : 'Not set'}
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visa Tiers</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{pricing.packages.length}</p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Max Visas</p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">{maxVisa}</p>
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Additional Charges</p>
                          {additionalChargeCount === 0 ? (
                            <p className="text-sm text-slate-600">No extra charges configured.</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(pricing.additionalCharges || {})
                                .slice(0, 8)
                                .map(([key, value]) => {
                                  const config = (pricing.chargeConfig || {})[key];
                                  const displayLabel = config?.label || key;
                                  const typeBadge = config?.type === 'flat_fee'
                                    ? 'Initial'
                                    : config?.type === 'flat_renewal_fee'
                                    ? 'Renewal'
                                    : null;
                                  return (
                                    <span
                                      key={key}
                                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700"
                                    >
                                      {displayLabel}: {formatAED(value)}
                                      {typeBadge && (
                                        <span className={`ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                          config.type === 'flat_fee'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-amber-100 text-amber-700'
                                        }`}>
                                          {typeBadge}
                                        </span>
                                      )}
                                    </span>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
              </div>
            )}
          </div>
        </section>

        <PricingStructureForm
          isOpen={showPricingForm}
          onClose={() => {
            setShowPricingForm(false);
            setEditingPricing(null);
          }}
          onSubmit={handlePricingSubmit}
          freezoneId={freezoneId}
          freezoneName={freezone?.name}
          freezoneCode={freezoneCode}
          editingData={editingPricing}
        />
      </div>
    </div>
  );
};

export default FreezoneDetail;
